import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRefs } from 'contexts/RefContext';
import { Group, Circle as RCircle } from 'react-konva';
import { Circle } from 'konva/lib/shapes/Circle';
import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';
import {
	setAzimuthOrPitchForNewlyCreatedNrelFacets, setDrawnFacetExists, setNewlyCreatedFacets, setUnfinalizedFacet, setGuideMode, setSnapMode,
	setActiveDragFunctionality, setMapOnlyFrozenPoint
} from 'store/slices/ToolSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { KonvaEventObject } from 'konva/lib/Node';
import { setDropdownState } from 'store/slices/EditFacetModalSlice';
import { calculateCentroidOfPolygon, calculateArrowPoints, KonvaGroupNames, calculateLineLength } from '../utils';
import {
	KONVA_FACET_GROUP,
	SEMI_TRANPARENT_BLACK_PANEL
} from '../../../constants';
import { convertMetricToLinearFeet, isModeDefault } from '../../../util';
import { getGroundMountDefaultPitchDegrees, getFacetDefaultRowSpacingInMeters, getFacetDefaultStartingHeightInFeet } from 'components/FacetInputs/util';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { useLongPress } from 'hooks/useLongPress';
import { appToast } from 'components/Basic/CustomToast';
import {
	AngleCallout, computeDrawFacetGuides, computeRelativeOrthogonalSnap, Guide, GUIDE_GROUP_NAME,
	PREVIEW_DOT_NAME, resetGuideLock, SnapGuideResult,
} from './DrawGuideLines';
import { NewlyCreatedFacet } from 'store/slices/ToolSlice/types';

const distance = (a: number[], b: number[]) => Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);

// maps global points to local space via a Konva transform — for Snap's cross-facet alignment.
function toLocalPoints(transform: { point: (p: Vector2d) => Vector2d }, points: number[][]): [number, number][] {
	return points.map((p): [number, number] => {
		const local = transform.point({ x: p[0], y: p[1] });
		return [local.x, local.y];
	});
}

// closing is invalid when all placed points lie on one line — the facet would have ~zero area
const isCollinearPolygon = (pts: number[]) => {
	const n = pts.length / 2;
	if (n < 3) return true;
	let area = 0;
	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n;
		area += pts[i * 2] * pts[j * 2 + 1] - pts[j * 2] * pts[i * 2 + 1];
	}
	return Math.abs(area / 2) < 1;
};

type ClosePoint = { index: number; point: [number, number] };

// Nearest vertex to close on (any vertex, not just the first).
// k <= n-3 keeps >= 3 points left; threshold <= 0 disables it (guide mode).
function findAutoClosePoint(points: number[], cursor: [number, number], threshold: number): ClosePoint | null {
	const n = points.length / 2;
	if (threshold <= 0 || n < 3) return null;

	let best: ClosePoint | null = null;
	let bestDist = Infinity;
	for (let k = 0; k <= n - 3; k++) {
		const vertex: [number, number] = [points[k * 2], points[k * 2 + 1]];
		const d = distance(cursor, vertex);
		if (d < threshold && d < bestDist) {
			best = { index: k, point: vertex };
			bestDist = d;
		}
	}
	return best;
}

// p1-p2 × p3-p4 intersection; excludes endpoint touches (that's a vertex match elsewhere).
function segmentIntersection(
	p1: [number, number], p2: [number, number], p3: [number, number], p4: [number, number],
): [number, number] | null {
	const d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
	const d2x = p4[0] - p3[0], d2y = p4[1] - p3[1];
	const denom = d1x * d2y - d1y * d2x;
	if (Math.abs(denom) < 1e-9) return null;
	const t = ((p3[0] - p1[0]) * d2y - (p3[1] - p1[1]) * d2x) / denom;
	const u = ((p3[0] - p1[0]) * d1y - (p3[1] - p1[1]) * d1x) / denom;
	const EPS = 1e-6;
	if (t < EPS || t > 1 - EPS || u < EPS || u > 1 - EPS) return null;
	return [p1[0] + t * d1x, p1[1] + t * d1y];
}

type CrossingClose = { edgeIndex: number; point: [number, number] };

// Click-time only, no hover preview. Crossing an earlier edge closes there, dropping the earlier part.
function findCrossingClosePoint(points: number[], cursor: [number, number]): CrossingClose | null {
	const n = points.length / 2;
	if (n < 3) return null;
	const last: [number, number] = [points[(n - 1) * 2], points[(n - 1) * 2 + 1]];

	let best: CrossingClose | null = null;
	let bestDist = Infinity;
	for (let i = 0; i <= n - 3; i++) {
		const a: [number, number] = [points[i * 2], points[i * 2 + 1]];
		const b: [number, number] = [points[(i + 1) * 2], points[(i + 1) * 2 + 1]];
		const hit = segmentIntersection(last, cursor, a, b);
		if (!hit) continue;
		const d = distance(last, hit);
		if (d < bestDist) { best = { edgeIndex: i, point: hit }; bestDist = d; }
	}
	return best;
}

const GUIDE_COLOR = '#ff6a00';
const SNAP_COLOR = '#22c55e';
const IS_TOUCH_DEVICE = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function clearGuideLines(parent: Konva.Group | null) {
	if (!parent) return;
	(parent.findOne(`.${GUIDE_GROUP_NAME}`) as Konva.Group | undefined)?.destroyChildren();
}

function clearPreviewDot(parent: Konva.Group | null) {
	if (!parent) return;
	parent.findOne(`.${PREVIEW_DOT_NAME}`)?.destroy();
}

function renderPreviewDot(parent: Konva.Group | null, x: number, y: number) {
	if (!parent || IS_TOUCH_DEVICE) return;
	clearPreviewDot(parent);
	parent.add(new Konva.Circle({
		name: PREVIEW_DOT_NAME,
		x,
		y,
		radius: 2,
		fill: 'rgba(0,0,0,0.4)',
		stroke:'rgba(0,0,0,0.4)',
		strokeWidth: 1,
		strokeScaleEnabled: false,
		listening: false,
	}));
}

type AngleCalloutAt = AngleCallout & { origin: [number, number] };

function renderGuideLines(
	parent: Konva.Group | null, guides: Guide[], zoomScale: number, halfLen: number, angleCallout?: AngleCalloutAt | null,
) {
	if (!parent) return;
	let group = parent.findOne(`.${GUIDE_GROUP_NAME}`) as Konva.Group | undefined;
	if (!group) {
		group = new Konva.Group({ name: GUIDE_GROUP_NAME, listening: false });
		parent.add(group);
	}
	group.destroyChildren();

	const scale = Math.max(zoomScale, 1e-6);
	for (const g of guides) {
		const dx = Math.cos((g.angleDeg * Math.PI) / 180);
		const dy = Math.sin((g.angleDeg * Math.PI) / 180);
		const [ox, oy] = g.origin;
		group.add(new Konva.Line({
			points: [ox - dx * halfLen, oy - dy * halfLen, ox + dx * halfLen, oy + dy * halfLen],
			stroke: GUIDE_COLOR,
			strokeWidth: 3,
			strokeScaleEnabled: false,
			dash: [10 / scale, 8 / scale],
			dashEnabled: true,
			listening: false,
			opacity: g.opacity,
		}));
	}

	// angle callout at the vertex: right-angle square for 90°, arc otherwise, plus the value.
	if (angleCallout) {
		const [ox, oy] = angleCallout.origin;
		const radius = 22 / scale;
		const rad1 = (angleCallout.startDeg * Math.PI) / 180;
		const rad2 = ((angleCallout.startDeg + angleCallout.sweepDeg) * Math.PI) / 180;

		if (Math.abs(angleCallout.angleDeg - 90) < 1) {
			const size = 16 / scale;
			const p1 = { x: ox + Math.cos(rad1) * size, y: oy + Math.sin(rad1) * size };
			const p2 = { x: ox + Math.cos(rad2) * size, y: oy + Math.sin(rad2) * size };
			const corner = { x: p1.x + Math.cos(rad2) * size, y: p1.y + Math.sin(rad2) * size };
			group.add(new Konva.Line({
				points: [p1.x, p1.y, corner.x, corner.y, p2.x, p2.y],
				stroke: GUIDE_COLOR,
				strokeWidth: 2,
				strokeScaleEnabled: false,
				listening: false,
			}));
		} else {
			group.add(new Konva.Arc({
				x: ox,
				y: oy,
				innerRadius: radius,
				outerRadius: radius,
				angle: angleCallout.sweepDeg,
				rotation: angleCallout.startDeg,
				stroke: GUIDE_COLOR,
				strokeWidth: 2,
				strokeScaleEnabled: false,
				listening: false,
			}));
		}

		const bisectorRad = ((angleCallout.startDeg + angleCallout.sweepDeg / 2) * Math.PI) / 180;
		const labelR = radius + 14 / scale;
		const fontSize = Math.max(15 / scale, 2);
		const label = new Konva.Text({
			x: ox + Math.cos(bisectorRad) * labelR,
			y: oy + Math.sin(bisectorRad) * labelR,
			text: `${Math.round(angleCallout.angleDeg)}°`,
			fontSize,
			fill: GUIDE_COLOR,
			fontStyle: 'bold',
			listening: false,
		});
		group.add(label);
		label.offsetX(label.width() / 2);
		label.offsetY(label.height() / 2);
	}

	group.moveToBottom();
	group.moveUp(); // above image, below facet lines
}

// SNAP mode visuals: reuse renderGuideLines for the constraint (drawn as a normal guide line),
// then add the green alignment line from the snapped point to the referenced node.
function renderSnapGuides(
	parent: Konva.Group | null,
	constraint: { origin: [number, number]; angleDeg: number },
	snapSegment: { from: [number, number]; to: [number, number] } | null,
	zoomScale: number,
	halfLen: number,
	angleCallout?: AngleCalloutAt | null,
) {
	if (!parent) return;
	renderGuideLines(parent, [{ ...constraint, type: 'perp', priority: 0, opacity: 0.9 }], zoomScale, halfLen, angleCallout);
	if (!snapSegment) return;

	const group = parent.findOne(`.${GUIDE_GROUP_NAME}`) as Konva.Group | undefined;
	if (!group) return;
	const scale = Math.max(zoomScale, 1e-6);
	group.add(new Konva.Line({
		points: [snapSegment.from[0], snapSegment.from[1], snapSegment.to[0], snapSegment.to[1]],
		stroke: SNAP_COLOR,
		strokeWidth: 3,
		strokeScaleEnabled: false,
		dash: [8 / scale, 6 / scale],
		dashEnabled: true,
		listening: false,
	}));
}

const lineConfig: Konva.LineConfig = {
	stroke: 'rgba(0,0,0,0.4)',
	strokeWidth: 3,
	fill: 'rgba(0,0,0,0.4)',
	strokeScaleEnabled: false,
	listening: true,
};

const textConfigBase: Konva.TextConfig = {
	fontFamily: 'Arial',
	fill: 'white',
	align: 'center',
	strokeScaleEnabled: false,
	strokeEnabled: true,
	listening: false,
	// shadowEnabled: true,
	// shadowOpacity: 0.5,
	// shadowForStrokeEnabled: true,
};

type DrawArrowProps = {
	polPoints: number[][];
	center: Vector2d;
	userFacetGroup: Konva.Group;
	konvaRef: React.RefObject<Konva.Stage>;
	dispatchCallback: (id: string, azimuth: number) => void,
};

type ArrowData = {
	angle: number;
	groupRefId: string;
	selected: boolean;
	center: Konva.Vector2d;
};

function createOrUpdateLengthText(
	linePts: number[][],
	line: Konva.Line,
	edgeIndex: number,
	group: Konva.Group | null,
	groundMetersPerPixel: number,
	scale: Vector2d,
	name: string,
) {
	const [startPt, endPt] = linePts;
	const pixelLength = calculateLineLength(startPt, endPt);
	const absolutePixelLength = pixelLength / scale.x;

	const baseFontSize = 20;
	const zoomScale = group?.getStage()?.getAbsoluteScale()?.x ?? 1;
	const fontSize = Math.max(baseFontSize / zoomScale, 2);

	// Convert length to feet
	const feetLength = convertMetricToLinearFeet((absolutePixelLength * groundMetersPerPixel));
	// Midpoint
	const midX = (startPt[0] + endPt[0]) / 2;
	const midY = (startPt[1] + endPt[1]) / 2;

	// Rotation angle
	let angle = Math.atan2(endPt[1] - startPt[1], endPt[0] - startPt[0]) * (180 / Math.PI);
	if (angle > 90 || angle < -90) angle += 180;

	// Perpendicular vector
	const rad = (angle * Math.PI) / 180;
	const nx = -Math.sin(rad);
	const ny = Math.cos(rad);

	// Offset away from line
	const gapWorld = 8 / zoomScale;
	const halfHeightWorld = (fontSize / 2) / zoomScale;
	const dx = nx * (halfHeightWorld + gapWorld);
	const dy = ny * (halfHeightWorld + gapWorld);

	const scaledLength = absolutePixelLength * zoomScale;

	const textConfig: Konva.TextConfig = {
		...textConfigBase,
		x: midX + dx,
		y: midY + dy,
		text: feetLength,
		rotation: angle,
		listening: false,
		strokeScaleEnabled: false,
		fontSize,
		name,
		visible: scaledLength > 28,
	};
	let lengthTextNode = group?.findOne(`.${name}`) as Konva.Text;
	if (!lengthTextNode) {
		lengthTextNode = new Konva.Text(textConfig);
		group?.add(lengthTextNode);
	} else {
		lengthTextNode.setAttrs({ ...textConfig });
	}
	lengthTextNode.setAttrs({
		edgeIndex,
		edgeLineId: line.id(),
	});

	// Now that it's in the scene graph, measure and center it
	const w = lengthTextNode.width();
	const h = lengthTextNode.height();
	lengthTextNode.offsetX(w / 2);
	lengthTextNode.offsetY(h / 2);
	lengthTextNode.moveToTop();
	return lengthTextNode;
}

// Function to draw arrows with updated types
function DrawArrows({ polPoints, center, userFacetGroup, konvaRef, dispatchCallback }: DrawArrowProps) {
	// Function to create an arrow
	const createArrow = (points: number[], customData: ArrowData, rectConfig: Konva.RectConfig): Konva.Group => {
		const rect = new Konva.Rect({
			...rectConfig,
			visible: true,
			stroke: SEMI_TRANPARENT_BLACK_PANEL,
			strokeWidth: 1,
			fill: SEMI_TRANPARENT_BLACK_PANEL,
			cornerRadius: [0, 2, 2, 0],
			strokeScaleEnabled: false,
		});

		const arrow = new Konva.Arrow({
			points,
			pointerLength: 4,
			pointerWidth: 4,
			fill: 'white',
			stroke: 'white',
			strokeWidth: 1,
			hitStrokeWidth: 20,
			strokeScaleEnabled: false,
		});

		const group = new Konva.Group({ customData, name: 'azimuth_selector' });
		group.add(rect);
		group.add(arrow);

		return group;
	};

	// Function to handle arrow click event
	const handleArrowClick = (arrowWithRect: Konva.Group, data: ArrowData) => {
		arrowWithRect.on('click', (e) => {
			e.cancelBubble = true;
			const { angle, groupRefId } = data;
			dispatchCallback(groupRefId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0], angle);
			selectArrow(arrowWithRect, userFacetGroup);
		});

		arrowWithRect.on('tap', (e) => {
			e.cancelBubble = true;
			const { angle, groupRefId } = data;
			dispatchCallback(groupRefId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0], angle);
			selectArrow(arrowWithRect, userFacetGroup);
		});

		arrowWithRect.on('mouseenter', () => {
			if (arrowWithRect.attrs?.customData?.selected) return;
			toggleArrowHighlight(arrowWithRect, true);
		});

		arrowWithRect.on('mouseleave', () => {
			if (arrowWithRect?.attrs?.customData?.selected) return;
			toggleArrowHighlight(arrowWithRect, false);
		});
	};

	// Function to select arrow and handle visibility
	const selectArrow = (arrowWithRect: Konva.Group, group: Konva.Group) => {
		const data = arrowWithRect.getAttr('customData') as ArrowData;
		arrowWithRect.setAttr('customData', { ...data, selected: true, });
		toggleArrowHighlight(arrowWithRect, true);

		group.children?.forEach((child) => {
			if (child.getClassName() === 'Group' && child.hasName('azimuth_selector') && child._id !== arrowWithRect._id) {
				const childData = child.getAttr('customData') as ArrowData;
				if (childData?.selected) {
					child.setAttr('customData', { ...childData, selected: false });
					toggleArrowHighlight(child as Konva.Group, false);
				}
			}
		});
	};

	//Function to toggle Highlight of arrows
	const toggleArrowHighlight = (arrowWithRect: Konva.Group, highlight = false) => {
		const rectColor = highlight ? '#ffc005' : SEMI_TRANPARENT_BLACK_PANEL;
		const arrowColor = highlight ? 'black' : 'white';
		if (!arrowWithRect.getChildren()?.length) return;
		const [rect, arrow] = arrowWithRect.getChildren() as [Konva.Rect, Konva.Arrow];
		rect.stroke(rectColor);
		rect.fill(rectColor);
		arrow.stroke(arrowColor);
		arrow.fill(arrowColor);
	};

	// Iterate through points and create arrows
	for (let i = 0; i < polPoints.length; i++) {
		const j = (i + 1) % polPoints.length;
		const { arrow: ap, rectangle } = calculateArrowPoints([polPoints[i], polPoints[j]], center);

		const arrowData: ArrowData = {
			angle: ap.angle,
			groupRefId: userFacetGroup.id(),
			selected: false,
			center,
		};

		const arrowWithRect = createArrow([...ap.outwardPoints[0], ...ap.outwardPoints[1]], arrowData, rectangle);

		// Handle the first arrow as the center arrow
		if (i === 0) {
			const { angle, groupRefId } = arrowData;
			dispatchCallback(groupRefId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0], angle);
			selectArrow(arrowWithRect, userFacetGroup); // This ensures the first arrow is selected and blue
		}

		handleArrowClick(arrowWithRect, arrowData);
		userFacetGroup.add(arrowWithRect);
		// hide edge length text in userFacetGroup
		userFacetGroup.getChildren(n => n.getClassName() === 'Text').forEach(t => t.visible(false));
	}
}

type Props = {
	showAzimuthArrows: boolean;
}

function updateAllFacetEdgeTexts(
	stage: Konva.Stage,
	scale: Vector2d
) {
	const zoomScale = stage.getAbsoluteScale().x;
	const baseFontSize = 20;

	stage.find('Text').forEach((node) => {
		if (!(node instanceof Konva.Text)) return;

		const group = node.getParent();
		if (!(group instanceof Konva.Group)) return;

		const lineId = node.getAttr('edgeLineId');
		const edgeIndex = node.getAttr('edgeIndex');
		if (lineId == null || edgeIndex == null) return;

		const line = group.findOne(`#${lineId}`) as Konva.Line;
		if (!line) return;

		if (!(line instanceof Konva.Line)) return;
		if (typeof line.points !== 'function') return;

		const pts = line.points();
		const i = edgeIndex * 2;
		const startPt: [number, number] = [pts[i], pts[i + 1]];
		let endPt: [number, number];
		// normal edges
		if (i + 3 < pts.length) {
			endPt = [pts[i + 2], pts[i + 3]];
		} 
		// closing edge (last - first)
		else {
			endPt = [pts[0], pts[1]];
		}

		// update the font size
		const fontSize = Math.max(baseFontSize / zoomScale, 2);
		node.fontSize(fontSize);

		// update the midpoint
		const midX = (startPt[0] + endPt[0]) / 2;
		const midY = (startPt[1] + endPt[1]) / 2;

		// updated the angle
		let angle =
      (Math.atan2(endPt[1] - startPt[1], endPt[0] - startPt[0]) * 180) /
      Math.PI;
		if (angle > 90 || angle < -90) angle += 180;

		// update the perpendicular offset
		const rad = (angle * Math.PI) / 180;
		const nx = -Math.sin(rad);
		const ny = Math.cos(rad);

		const gapWorld = 8 / zoomScale;
		const halfHeightWorld = (fontSize / 2) / zoomScale;

		node.position({
			x: midX + nx * (halfHeightWorld + gapWorld),
			y: midY + ny * (halfHeightWorld + gapWorld),
		});

		node.rotation(angle);

		const pixelLength = calculateLineLength(startPt, endPt);
		const absolutePixelLength = pixelLength / scale.x;
		node.visible(absolutePixelLength * zoomScale > 28);

		// recenter
		node.offsetX(node.width() / 2);
		node.offsetY(node.height() / 2);
	});
}

export default function PanelDrawer(props: Props) {
	const { editModeEnabled, drawModeEnabled, moveStage, rotateStage, newlyCreatedFacets,
		groundMountEnabled, noGapGroundMountEnabled, guideMode, snapMode, hideSatelliteOverlay,
	} = useSelector((state: RootState) => state.toolNewPostions.data, shallowEqual);
	const { mode, groundMetersPerPixel, designType, org } = useSelector((state: RootState) => state.roofData.data, shallowEqual);
	const { konvaRef } = useRefs();
	const groupRef = useRef<Konva.Group | null>(null);
	const userFacetGroup = useRef<Konva.Group | null>(null);
	const isDrawing = useRef<boolean>(false);
	const dispatch = useDispatch<AppDispatch>();
	const [lastSnappedPosition, setlastSnappedPosition] = useState<null | { x: number, y: number }>(null);
	const preivewLengthTextRef = useRef<Konva.Text | null>(null);
	const [multiFingersTouched, setMultiFingersTouched] = useState(false);
	const guideModeRef = useRef(guideMode);
	const snapModeRef = useRef(snapMode);
	// tracks "Shift is down because our own keydown handler put it there" — true for BOTH
	// the Guide-only and Snap (Shift+S) branches, not just Guide.
	const shiftActivatedRef = useRef(false);
	const snapActivatedRef = useRef(false);
	const guideSnappedPositionRef = useRef<{ x: number; y: number } | null>(null);
	const suppressGuidesRef = useRef(false);
	const isTouchInteractionRef = useRef(IS_TOUCH_DEVICE);
	// Konva loses pointer position on mouseleave — fallback for that case.
	const lastRealPointerPosRef = useRef<Vector2d | null>(null);
	// lets recomputeGuidePreview call the latest handleMouseMove (declared later below).
	const handleMouseMoveRef = useRef<(() => void) | null>(null);
	// tracks guide/snap active state across renders, for the toolbar-button effect below.
	const wasActiveRef = useRef(false);

	const canDrawFacet = editModeEnabled && drawModeEnabled && !moveStage && !rotateStage && !hideSatelliteOverlay;
	const scale = useKonvaImageScale();

	const { handleTouchStart, handleTouchEndOrCancel, handleTouchMove, longTouchTimerRef, longPressTriggeredRef } = useLongPress({
		delay: 800, // ms
		enabled: canDrawFacet,
		onLongPress: openDeleteFacetPopup,
	});

	useEffect(() => {
		guideModeRef.current = guideMode;
	}, [guideMode]);

	useEffect(() => {
		snapModeRef.current = snapMode;
	}, [snapMode]);

	// When map only turns on mid-draw, snapshot the frozen preview endpoint into the global
	// store and register this functionality; on exit, clear both. handleMouseMove ignores
	// pointer moves while hidden, so the in-progress line stays exactly as it was left and
	// resumes following on the first real mouse move after exit.
	const wasHiddenRef = useRef(hideSatelliteOverlay);
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDrawing.current) return;

		if (hideSatelliteOverlay && !wasHidden) {
			dispatch(setActiveDragFunctionality('drawFacet'));
			dispatch(setMapOnlyFrozenPoint(lastRealPointerPosRef.current));
		} else if (!hideSatelliteOverlay && wasHidden) {
			dispatch(setActiveDragFunctionality(null));
			dispatch(setMapOnlyFrozenPoint(null));
		}
	}, [hideSatelliteOverlay, dispatch]);

	// Toggling doesn't move the mouse, so nothing redraws automatically — recompute and repaint now.
	const recomputeGuidePreview = useCallback(() => {
		handleMouseMoveRef.current?.();
		konvaRef?.current?.batchDraw();
	}, [konvaRef]);

	// Sync refs immediately (no redux round-trip), then repaint. Reset lock only on fresh enable.
	const activateGuide = useCallback(() => {
		if (!guideModeRef.current && !snapModeRef.current) resetGuideLock();
		if (!guideModeRef.current) dispatch(setGuideMode(true));
		guideModeRef.current = true;
		recomputeGuidePreview();
	}, [dispatch, recomputeGuidePreview]);

	const activateSnap = useCallback(() => {
		if (!guideModeRef.current && !snapModeRef.current) resetGuideLock();
		if (!snapModeRef.current) dispatch(setSnapMode(true));
		snapModeRef.current = true;
		guideModeRef.current = true;
		recomputeGuidePreview();
	}, [dispatch, recomputeGuidePreview]);

	const deactivateSnap = useCallback(() => {
		if (snapModeRef.current) dispatch(setSnapMode(false));
		snapModeRef.current = false;
		recomputeGuidePreview();
	}, [dispatch, recomputeGuidePreview]);

	const deactivateGuideAndSnap = useCallback(() => {
		if (guideModeRef.current) dispatch(setGuideMode(false));
		guideModeRef.current = false;
		snapModeRef.current = false;
		recomputeGuidePreview();
	}, [dispatch, recomputeGuidePreview]);

	useEffect(() => {
		// shared by keyup and blur: drop both keyboard-activated modes and their tracking refs
		const resetModes = () => {
			if (shiftActivatedRef.current && guideModeRef.current) deactivateGuideAndSnap();
			if (snapActivatedRef.current && snapModeRef.current) deactivateSnap();
			shiftActivatedRef.current = false;
			snapActivatedRef.current = false;
		};

		// no toLowerCase: autofill sends key-less events
		const isSKey = (e: KeyboardEvent) => e.key === 's' || e.key === 'S';

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Shift') {
				// Shift alone => guide only
				shiftActivatedRef.current = true;
				activateGuide();
				return;
			}

			// S while Shift is held => snap
			if (isSKey(e) && e.shiftKey) {
				snapActivatedRef.current = true;
				shiftActivatedRef.current = true;
				activateSnap();
			}
		};
		const onKeyUp = (e: KeyboardEvent) => {
			// releasing Shift drops both keyboard-activated modes
			if (e.key === 'Shift') resetModes();
			// releasing S drops snap, guide stays on
			if (isSKey(e)) {
				if (snapActivatedRef.current && snapModeRef.current) deactivateSnap();
				snapActivatedRef.current = false;
			}
		};
		// losing window focus mid-combo (alt-tab, a stolen dialog, or a platform that
		// swallows a keyup while another modifier is held) can leave these modes stuck on.
		window.addEventListener('keydown', onKeyDown);
		window.addEventListener('keyup', onKeyUp);
		window.addEventListener('blur', resetModes);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
			window.removeEventListener('keyup', onKeyUp);
			window.removeEventListener('blur', resetModes);
		};
	}, [activateGuide, activateSnap, deactivateGuideAndSnap, deactivateSnap]);

	useEffect(() => {
		// covers the toolbar buttons, which dispatch redux directly instead of using the above.
		const isActive = guideMode || snapMode;
		if (isActive && !wasActiveRef.current) resetGuideLock();
		wasActiveRef.current = isActive;
		recomputeGuidePreview();
	}, [guideMode, snapMode, recomputeGuidePreview]);

	const getAlreadyExistingFacetsPoints = useCallback(() => {
		const points: number[][] = [];
		if(!drawModeEnabled) return points;
		// eslint-disable-next-line no-constant-condition
		const lineSelector = true
			? `${KonvaGroupNames.existingFacetsGroup}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`
			: 'simplified-hull';
		const lines = konvaRef?.current?.find(`.${lineSelector}`) ?? [];

		lines.forEach((node) => {
			if (node instanceof Konva.Line) {
				const transform = node.getAbsoluteTransform();
				const localPoints = node.points();
				for (let i = 0; i < localPoints.length; i += 2) {
					const local = { x: localPoints[i], y: localPoints[i + 1] };
					const global = transform.point(local);
					points.push([global.x, global.y]);
				}
			}
		});
		return points;
	},[drawModeEnabled, konvaRef]);

	// corner nodes (global) of every OTHER facet — for vertex snapping and Snap's cross-facet alignment.
	const getOtherFacetsPoints = useCallback((): number[][] => {
		const userdrawnfacetPoints: number[][] = [];
		// read live from canvas — an undone/deleted facet is destroyed there, so it can't linger
		const userDrawnGroups = (groupRef.current?.find(`.${KonvaGroupNames.inProgressUserDefinedFacetGroup}`) ?? []) as Konva.Group[];
		userDrawnGroups.forEach((group) => {
			const line = group.getChildren((n) => n.getClassName() === 'Line')
				.find((n) => (n as Konva.Line).closed()) as Konva.Line | undefined;
			if (!line) return;
			const transform = line.getAbsoluteTransform();
			const points = line.points();
			for (let i = 0; i < points.length; i += 2) {
				const point = transform.point({ x: points[i], y: points[i + 1] });
				userdrawnfacetPoints.push([point.x, point.y]);
			}
		});
		return [...getAlreadyExistingFacetsPoints(), ...userdrawnfacetPoints];
	}, [getAlreadyExistingFacetsPoints]);

	useEffect(() => {
		const panelDrawerChildren = groupRef.current?.children ?? [];

		if (!drawModeEnabled) {
			setlastSnappedPosition(null);
			guideSnappedPositionRef.current = null;
			isDrawing.current = false;
			userFacetGroup.current = null;
		}

		if (panelDrawerChildren.length > 0) {
			for (let i = panelDrawerChildren.length - 1; i >= 0; i--) {
				const child = panelDrawerChildren[i];
				if (child instanceof Konva.Image) continue;
				child.destroy();
			}
			userFacetGroup.current = null;
			isDrawing.current = false;
			setlastSnappedPosition(null);
		}
	}, [drawModeEnabled, konvaRef]);

	const dispatchNewlyCreatedFacets= useCallback((facetPoints: number[][]) => {
		const newlyCreatedFacet: NewlyCreatedFacet = {
			hullCoords: facetPoints,
			konvaPolygonId: userFacetGroup.current!.id().split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0],
			isGroundMount: groundMountEnabled,
			noGapGroundMount: noGapGroundMountEnabled,
		};
		if (newlyCreatedFacet.isGroundMount) {
			newlyCreatedFacet.startingHeight = getFacetDefaultStartingHeightInFeet();
			if (!newlyCreatedFacet.noGapGroundMount) {
				newlyCreatedFacet.pitchDegrees = getGroundMountDefaultPitchDegrees({
					isSolarDesign: designType === 'SOLAR',
					defaultFlatPitchDeg: org.setting.defaultFlatPitchDeg,
				}),
				newlyCreatedFacet.rowSpacing = getFacetDefaultRowSpacingInMeters(
					newlyCreatedFacet.pitchDegrees,
					groundMetersPerPixel,
				);
			}
		}
		dispatch(setNewlyCreatedFacets({newlyCreatedFacet}));
	},[designType, dispatch, groundMetersPerPixel, groundMountEnabled, noGapGroundMountEnabled, org.setting.defaultFlatPitchDeg]);

	function makeOtherPolygonsInvisbleWhileUserIsDrawing(opacity: number) {
		groupRef.current?.children?.forEach(c => {
			if (c instanceof Konva.Image) return;
			c.opacity(opacity);
		});
	}

	const handlePolygonClick = useCallback((e: KonvaEventObject<MouseEvent>) => {
		e.evt.preventDefault();
		const lineId = e.target.getAttr('id');
		dispatch(setUnfinalizedFacet({ id: lineId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0] }));
		dispatch(setDropdownState({ open: true, position: { x: e.evt.clientX, y: e.evt.clientY } }));
	},[dispatch]);

	// Closes with finalPoints (may drop earlier points); rebuilds markers since indices shift.
	const closeFacet = useCallback((finalPoints: number[]) => {
		if (!konvaRef?.current) return; // narrows konvaRef for DrawArrows below
		const group = userFacetGroup.current;
		const facetLine = group?.children?.[0] as Konva.Line;
		if (!group || !facetLine) return;

		group.findOne('.preview-line')?.remove();
		group.getChildren(n => n.getClassName() === 'Circle' || n.getClassName() === 'Text').forEach(n => n.destroy());

		facetLine.points(finalPoints);
		facetLine.closed(true);
		group.setAttr('closed', true);

		const pairs: number[][] = [];
		for (let i = 0; i < finalPoints.length; i += 2) {
			pairs.push([finalPoints[i], finalPoints[i + 1]]);
			addPoint(finalPoints[i], finalPoints[i + 1]);

			const next = (i + 2) % finalPoints.length;
			createOrUpdateLengthText(
				[[finalPoints[i], finalPoints[i + 1]], [finalPoints[next], finalPoints[next + 1]]],
				facetLine,
				i / 2,
				group,
				groundMetersPerPixel,
				scale,
				`${[finalPoints[i], finalPoints[i + 1], finalPoints[next], finalPoints[next + 1]].join('-')}-facet-line-text`,
			);
		}

		clearGuideLines(groupRef.current);
		clearPreviewDot(groupRef.current);
		setlastSnappedPosition(null);
		guideSnappedPositionRef.current = null;

		makeOtherPolygonsInvisbleWhileUserIsDrawing(1);
		dispatchNewlyCreatedFacets(pairs);
		if (props.showAzimuthArrows) {
			DrawArrows({
				polPoints: pairs,
				center: calculateCentroidOfPolygon(finalPoints.slice()),
				userFacetGroup: group,
				konvaRef,
				dispatchCallback: (id, azimuth) => {
					dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({ facetId: id, azimuthDegrees: azimuth }));
				}
			});
		}
		dispatch(setUnfinalizedFacet({ id: group.id().split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0] }));
		isDrawing.current = false;
	}, [dispatch, dispatchNewlyCreatedFacets, groundMetersPerPixel, konvaRef, props.showAzimuthArrows, scale]);

	const makePolygon = useCallback(() => {
		if (!konvaRef?.current) return;
		// guide snaps are only meaningful relative to a facet in progress
		const guideSnap = isDrawing.current ? guideSnappedPositionRef.current : null;
		const snap = lastSnappedPosition ?? guideSnap;
		let { x, y } = konvaRef.current.getRelativePointerPosition() as Vector2d;

		if (snap) {
			x = snap.x;
			y = snap.y;
		}

		// if (groupRef.current?.children?.length) {
		// 	//update drwaing state when no userfacetgroup or all are completed
		// 	const children = groupRef.current.children;
		// 	if (children.length === 1) {
		// 		isDrawing.current = false;
		// 	} else {
		// 		const groups = groupRef.current.find(`.${KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME}`);
		// 		const lastGroupChild = groups[groups.length - 1];
		// 		isDrawing.current = lastGroupChild ? !lastGroupChild.attrs?.closed: false;
		// 	}
		// }
		if (isDrawing.current && !userFacetGroup.current?.getParent()) {
			isDrawing.current = false;
			userFacetGroup.current = null;
		}

		//  --------------- initializing userFacetGroup -----------
		if (isDrawing.current === false) {
			userFacetGroup.current = null;
			//Create new Facet Group having circles and lines
			dispatch(setDrawnFacetExists({ exists: true }));
			const uuid = crypto.randomUUID();
			userFacetGroup.current = new Konva.Group({
				name: KonvaGroupNames.inProgressUserDefinedFacetGroup,
				id: uuid + KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME
			});
			const previewLine = new Konva.Line({ name: 'preview-line', ...lineConfig, points: [], strokeScaleEnabled: false });
			const facetLine = new Konva.Line(
				{
					...lineConfig,
					points: [],
					id: uuid + KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KONVA_FACET_GROUP.LINE_ID_SUFFIX,
					strokeScaleEnabled: false
				});

			userFacetGroup?.current?.add(facetLine);
			userFacetGroup?.current?.add(previewLine);
			groupRef.current?.add(userFacetGroup.current as Konva.Group);
		}

		const facetLine = userFacetGroup?.current?.children?.[0] as Konva.Line;
		const points = facetLine?.points() ?? [];

		// shared: reject a zero-area result, else close
		const attemptClose = (finalPoints: number[]) => {
			if (isCollinearPolygon(finalPoints)) {
				appToast.warn('It\'s not a valid move to create a facet', {
					toastId: 'invalid-facet-close',
					position: 'bottom-center',
				});
			} else {
				closeFacet(finalPoints);
			}
		};

		// (x, y) here is the same point the preview line already showed

		// click near any vertex closes there, dropping earlier points
		const autoClose = findAutoClosePoint(points, [x, y], 5);

		// use the vertex's exact point, not the raw click — click jitter of a few px can
		// otherwise graze the vertex's own edge and register as a false crossing
		const crossing = findCrossingClosePoint(points, autoClose ? autoClose.point : [x, y]);
		if (crossing) {
			attemptClose([...points.slice((crossing.edgeIndex + 1) * 2), crossing.point[0], crossing.point[1]]);
			return;
		}

		if (autoClose) {
			attemptClose(points.slice(autoClose.index * 2));
			return;
		}

		// ----Add text node for every point added to the facet line---
		// ---TO_DO: add line and its length text in one grp for better undo/redo---
		if(points.length >= 2 ){
			const linePoints = [[points[points.length - 2], points[points.length - 1]], [x, y]];
			const edgeIndex = (points.length / 2) - 1;
			createOrUpdateLengthText(
				linePoints,
				facetLine,
				edgeIndex,
				userFacetGroup.current,
				groundMetersPerPixel,
				scale,
				`${linePoints.flat().join('-')}-facet-line-text`,
			);
		}

		points.push(+x, +y);

		clearGuideLines(groupRef.current);
		clearPreviewDot(groupRef.current);
		addPoint(x, y);
		setlastSnappedPosition(null);
		guideSnappedPositionRef.current = null;
		facetLine.points(points);
		facetLine.on('contextmenu', handlePolygonClick);
		facetLine.on('touchstart', (e) => handleTouchStart(e, { segmentId: facetLine.id() }));
		facetLine.on('touchend', handleTouchEndOrCancel);
		facetLine.on('touchmove', handleTouchMove);
		facetLine.on('touchcancel', handleTouchEndOrCancel);
	},[closeFacet, dispatch,
		groundMetersPerPixel, handlePolygonClick,
		handleTouchEndOrCancel, handleTouchMove, handleTouchStart, konvaRef, lastSnappedPosition, scale]);

	const startFacetFromSnapPoint = useCallback((x: number, y: number) => {
		dispatch(setDrawnFacetExists({ exists: true }));
		const uuid = crypto.randomUUID();

		userFacetGroup.current = new Konva.Group({
			name: KonvaGroupNames.inProgressUserDefinedFacetGroup,
			id: uuid + KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME
		});

		const previewLine = new Konva.Line({
			name: 'preview-line',
			...lineConfig,
			points: [],
			strokeScaleEnabled: false
		});

		const facetLine = new Konva.Line({
			...lineConfig,
			points: [],
			id: uuid + KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KONVA_FACET_GROUP.LINE_ID_SUFFIX,
			strokeScaleEnabled: false
		});

		userFacetGroup.current.add(facetLine);
		userFacetGroup.current.add(previewLine);
		groupRef.current?.add(userFacetGroup.current);

		addPoint(x, y);
		facetLine.points([x, y]);
		isDrawing.current = true;
		setlastSnappedPosition({ x, y });
	},[dispatch]);

	function addPoint(x: number, y: number) {
		userFacetGroup.current?.add(new Circle({
			x: x,
			y: y,
			radius: 2,
			strokeScaleEnabled: false,
			fill: 'blue',
			stroke: 'black',
			strokeWidth: 1,
		}));
		isDrawing.current = true;
	}

	// shared setup for both guide/snap renderers: current zoom + the guide-line half-length
	const getGuideRenderContext = useCallback(() => {
		const stage = konvaRef?.current;
		if (!stage || !groupRef.current) return null;
		const zoomScale = groupRef.current.getAbsoluteScale().x || 1;
		const halfLen = (stage.width() + stage.height()) / Math.max(zoomScale, 1e-6);
		return { zoomScale, halfLen };
	}, [konvaRef]);

	const renderGuidesAtCursor = useCallback((cursor: Vector2d, points: number[]) => {
		const ctx = getGuideRenderContext();
		if (!ctx) return null;
		const { guides, snapped, angleCallout } = computeDrawFacetGuides({ cursor, points, zoomScale: ctx.zoomScale, isTouch: IS_TOUCH_DEVICE });
		if (snapped) {
			const calloutAt = angleCallout ? { ...angleCallout, origin: guides[0].origin } : null;
			renderGuideLines(groupRef.current, guides, ctx.zoomScale, ctx.halfLen, calloutAt);
		} else {
			clearGuideLines(groupRef.current);
		}
		return snapped;
	}, [getGuideRenderContext]);

	// SNAP mode: relative-angle constraint + node alignment (this facet's own nodes plus
	// externalNodes from other facets), with a green guide line.
	const renderSnapAtCursor = useCallback((
		cursor: Vector2d, points: number[], externalNodes: [number, number][],
	): SnapGuideResult['snapped'] | null => {
		const ctx = getGuideRenderContext();
		if (!ctx) return null;
		const res = computeRelativeOrthogonalSnap({ cursor, points, zoomScale: ctx.zoomScale, externalNodes });
		if (res) {
			const calloutAt = res.angleCallout ? { ...res.angleCallout, origin: res.constraint.origin } : null;
			renderSnapGuides(groupRef.current, res.constraint, res.snapSegment, ctx.zoomScale, ctx.halfLen, calloutAt);
		} else {
			clearGuideLines(groupRef.current);
		}
		return res ? res.snapped : null;
	}, [getGuideRenderContext]);

	const handleMouseMove = useCallback((e?: KonvaEventObject<MouseEvent>) => {
		// map only: stage mousemove keeps firing even with the layer hidden — freeze the
		// preview at its last position so exiting shows exactly what was left, and let the
		// next real mouse move after exit resume the follow naturally.
		if (hideSatelliteOverlay) return;
		// e is only set for real pointer events, not synthetic recompute calls
		if (e?.evt) isTouchInteractionRef.current = false;
		const stage = konvaRef?.current?.getStage();
		if(!stage || !groupRef.current) return;
		const snappingPos = stage?.getPointerPosition() ?? lastRealPointerPosRef.current;
		if (!snappingPos) return;
		if (e?.evt) lastRealPointerPosRef.current = snappingPos;

		let snappedGlobal: Vector2d | null = null;
		const stageScale = stage.scaleX() || 1;
		const SNAP_THRESHOLD = snapModeRef.current ? 10 : guideModeRef.current ? 0 : 5;
		const vertexSnapThreshold = snapModeRef.current ? 10 * stageScale : guideModeRef.current ? 0 : 5;
		// other facets' corner nodes (global) — reused below for Snap's cross-facet alignment
		let otherFacetsPoints: number[][] = [];

		// Guide mode pins this to 0 (pure angle-lock, no vertex magnetism) — skip the
		// point collection/transform work entirely since no distance can ever qualify.
		if (vertexSnapThreshold > 0) {
			otherFacetsPoints = getOtherFacetsPoints();

			// Among all vertices within the threshold, snap to the one nearest the cursor
			let snappedDist = Infinity;
			for (const [px, py] of otherFacetsPoints) {
				const dist = Math.hypot(snappingPos.x - px, snappingPos.y - py);
				if (dist < vertexSnapThreshold && dist < snappedDist) {
					snappedGlobal = { x: px, y: py };
					snappedDist = dist;
				}
			}
		}

		const parentGroup = userFacetGroup.current?.hasChildren() ? userFacetGroup.current : groupRef.current;
		if(!parentGroup) return;

		const groupTransform = parentGroup.getAbsoluteTransform().copy().invert();
		const localPos = parentGroup.getRelativePointerPosition();
		if (!localPos) return;

		// Fallback to current mouse local position
		let { x, y } = localPos;

		// Use snapped position if available
		if (snappedGlobal) {
			const snappedLocal = groupTransform.point(snappedGlobal);
			x = snappedLocal.x;
			y = snappedLocal.y;
			setlastSnappedPosition({ x, y });
		} else {
			setlastSnappedPosition(null);
		}

		if(!userFacetGroup.current) return;
		const facetLine = userFacetGroup.current.children?.[0] as Konva.Line;
		const previewLine = userFacetGroup.current.findOne('.preview-line') as Konva.Line;
		const points = facetLine?.points() ?? [];

		if (isDrawing.current && points.length >= 2) {
			let closeSnapped = false;
			// preview the close on the nearest earlier vertex, not just the first
			const autoClose = findAutoClosePoint(points, [localPos.x, localPos.y], SNAP_THRESHOLD);
			if (autoClose) {
				// other-facet snap wins if it's closer than the close point
				const distToClose = Math.hypot(localPos.x - autoClose.point[0], localPos.y - autoClose.point[1]);
				const vertexIsCloser = !!snappedGlobal && Math.hypot(localPos.x - x, localPos.y - y) < distToClose;
				if (!vertexIsCloser) {
					x = autoClose.point[0];
					y = autoClose.point[1];
					setlastSnappedPosition({ x, y });
					closeSnapped = true;
				}
			}

			// vertex/close snaps take priority; guide/snap only when neither is active.
			// snap mode (Shift+S) takes priority over guide mode (Shift).
			// suppressed during touch-drag: tap guides stay as-is until the next tap
			if (!suppressGuidesRef.current) {
				const canGuideOrSnap = !snappedGlobal && !closeSnapped;
				let activeSnap: { x: number; y: number } | null = null;
				if (canGuideOrSnap && snapModeRef.current) {
					const externalNodes = toLocalPoints(groupTransform, otherFacetsPoints);
					activeSnap = renderSnapAtCursor({ x, y }, points, externalNodes);
				}
				else if (canGuideOrSnap && guideModeRef.current) activeSnap = renderGuidesAtCursor({ x, y }, points);
				if (activeSnap) { x = activeSnap.x; y = activeSnap.y; }
				else clearGuideLines(groupRef.current);
				guideSnappedPositionRef.current = activeSnap;
			}

			if (snappedGlobal || closeSnapped || isTouchInteractionRef.current) {
				clearPreviewDot(groupRef.current);
			} else {
				renderPreviewDot(groupRef.current, x, y);
			}

			const startX = points[points.length - 2];
			const startY = points[points.length - 1];
			const linePoints = [[startX, startY], [x, y]];
			previewLine?.points(linePoints.flat());
			previewLine?.visible(true);
			//Add Length text for preview line
			preivewLengthTextRef.current = createOrUpdateLengthText(
				linePoints,
				previewLine,
				0,
				userFacetGroup.current,
				groundMetersPerPixel,
				scale,
				'preview-line-text'
			);
		} else {
			previewLine?.visible(false);
			clearGuideLines(groupRef.current);
			clearPreviewDot(groupRef.current);
			if (preivewLengthTextRef.current) {
				preivewLengthTextRef.current.remove();
				preivewLengthTextRef.current = null;
			}
		}
	},[konvaRef, getOtherFacetsPoints, groundMetersPerPixel, scale, renderGuidesAtCursor, renderSnapAtCursor, hideSatelliteOverlay]);

	useEffect(() => {
		handleMouseMoveRef.current = handleMouseMove;
	}, [handleMouseMove]);

	function openDeleteFacetPopup(position: Vector2d, pointIndex: number, lineId: string){
		const line = konvaRef?.current?.findOne(`#${lineId}`) as Konva.Line | null;
		// Only allow delete popup for completed facets
		if (!line || !line.closed()) return;
		dispatch(setUnfinalizedFacet({ id: lineId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0] }));
		dispatch(setDropdownState({ open: true, position }));
	}

	// function handleTouchStart(e: KonvaEventObject<TouchEvent>) {
	// 	clearTimeout(longTouchTimerRef.current);
	// 	longTouchTimerRef.current = setTimeout(() => {
	// 		const lineId = e.target.getAttr('id');
	// 		dispatch(setUnfinalizedFacet({ id: lineId.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[0] }));
	// 		dispatch(setDropdownState({ open: true, position: { x: e.evt.touches[0].clientX, y: e.evt.touches[0].clientY } }));
	// 	}, 200);
	// }

	// function handleTouchEndOrCancel() {
	// 	clearTimeout(longTouchTimerRef.current);
	// }

	const totalCreateFacets = Object.keys(newlyCreatedFacets);

	const handleDrawEvent = useCallback((e: KonvaEventObject<MouseEvent | Event>) => {
		if (!canDrawFacet) return;
		if(multiFingersTouched) {
			setMultiFingersTouched(false);
			return;
		}
		if (!isModeDefault(mode) && totalCreateFacets.length >= 1) {
			return;
		}
		if (e.type === 'pointerclick') e.evt.preventDefault();
		if (!isDrawing.current && lastSnappedPosition) {
			const { x, y } = lastSnappedPosition;
			startFacetFromSnapPoint(x, y);
			handleMouseMove();
		} else {
			makePolygon();
		}
	},[canDrawFacet, handleMouseMove, lastSnappedPosition,
		makePolygon, mode, multiFingersTouched, startFacetFromSnapPoint,
		totalCreateFacets.length]);

	const handleTap = useCallback((e: KonvaEventObject<Event>) => {
		isTouchInteractionRef.current = true;
		// If a long-press just fired, ignore this tap so we don't add a point.
		if (longPressTriggeredRef.current) {
			longPressTriggeredRef.current = false;
			return;
		}
		if (longTouchTimerRef.current) {
			clearTimeout(longTouchTimerRef.current);
			longTouchTimerRef.current = null;
		}
		if ((guideModeRef.current || snapModeRef.current) && isDrawing.current) {
			handleMouseMove();
		}
		handleDrawEvent(e);
		// touch has no mousemove — show guide/snap lines from the just-placed anchor
		if ((guideModeRef.current || snapModeRef.current) && isDrawing.current && groupRef.current && userFacetGroup.current) {
			const parentGroup = userFacetGroup.current.hasChildren() ? userFacetGroup.current : groupRef.current;
			const localPos = parentGroup.getRelativePointerPosition();
			const facetLine = userFacetGroup.current.children?.[0] as Konva.Line | undefined;
			const points = facetLine?.points() ?? [];
			if (localPos && points.length >= 4) {
				let snap: { x: number; y: number } | null;
				if (snapModeRef.current) {
					const groupTransform = parentGroup.getAbsoluteTransform().copy().invert();
					const externalNodes = toLocalPoints(groupTransform, getOtherFacetsPoints());
					snap = renderSnapAtCursor(localPos, points, externalNodes);
				} else {
					snap = renderGuidesAtCursor(localPos, points);
				}
				if (snap) konvaRef?.current?.batchDraw();
				else guideSnappedPositionRef.current = null;
			}
		}
	}, [
		getOtherFacetsPoints, handleDrawEvent, handleMouseMove, konvaRef,
		longPressTriggeredRef, longTouchTimerRef, renderGuidesAtCursor, renderSnapAtCursor,
	]);

	useEffect(() => {
		if(!konvaRef) return;
		const stage = konvaRef.current;
		if (!stage) return;

		const handleScaleChange = () => {
			updateAllFacetEdgeTexts(stage, scale);
			stage.batchDraw();
		};

		stage.on('scaleXChange scaleYChange', handleScaleChange);

		return () => {
			stage.off('scaleXChange scaleYChange', handleScaleChange);
		};
	}, [konvaRef, scale]);

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage) return;

		const handleTouchStartStage = (e: KonvaEventObject<TouchEvent>) => {
			isTouchInteractionRef.current = true;
			if (e.evt.touches.length > 1) {
				setMultiFingersTouched(true);
			}
		};

		const handleTouchMoveGuide = (e: KonvaEventObject<TouchEvent>) => {
			if (e.evt.touches.length > 1) return;
			suppressGuidesRef.current = true;
			try {
				handleMouseMove();
			} finally {
				suppressGuidesRef.current = false;
			}
		};

		stage.on('touchstart', handleTouchStartStage);
		stage.on('click', handleDrawEvent);
		stage.on('tap', handleTap);
		stage.on('mousemove', handleMouseMove);
		stage.on('touchmove', handleTouchMoveGuide);

		return () => {
			stage.off('touchstart', handleTouchStartStage);
			stage.off('click', handleDrawEvent);
			stage.off('tap', handleTap);
			stage.off('mousemove', handleMouseMove);
			stage.off('touchmove', handleTouchMoveGuide);
		};
	}, [canDrawFacet, handleDrawEvent, handleMouseMove, handleTap, konvaRef]);

	return (

		<Group
			ref={groupRef}
			name={'panel-drawer'}
			// listening={canDrawFacet}
			// onTouchStart={(e: KonvaEventObject<TouchEvent>) => {
			// 	if(e.evt.touches.length > 1) {
			// 		setMultiFingersTouched(true);
			// 	}
			// }}
			// onClick={handleDrawEvent}
			// onTap={(e)=>{
			// 	if (longTouchTimerRef.current) {
			// 		clearTimeout(longTouchTimerRef.current!);
			// 		return;
			// 	}
			// 	handleDrawEvent(e);
			// }}
			// onMouseMove={handleMouseMove}
		>
			{lastSnappedPosition &&
				<RCircle
					x={lastSnappedPosition.x}
					y={lastSnappedPosition.y}
					radius={2}
					fill={'yellow'}
					stroke={'black'}
					strokeWidth={1}
					strokeScaleEnabled={false}
				/>
			}
		</Group>
	);
}
